import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { customStyles } from "../../../assets/table-style";
import { ThreeDot } from "react-loading-indicators";
import swal from "sweetalert";
import { format } from "date-fns";
import NoData from "../../../assets/img/no-data.png";
import NoAccess from "../../../assets/img/NoAccess.png";
import useDecodeToken from "../../../hooks/customHook/useDecodeToken";
import { formatToPHP } from "../../../utils/numberFormatter";
import { formatPeriod } from "../../../helper/format_period";
import { getRetainedNetIncomes } from "../../../services/RetainedNetIncome/getRetainedNetIncomes.service";
import { getProfitDistributions } from "../../../services/ProfitDistribution/getProfitDistributions.service";
import { getProfitDistributionById } from "../../../services/ProfitDistribution/getProfitDistributionById.service";
import { updateProfitDistribution } from "../../../services/ProfitDistribution/updateProfitDistribution.service";
import { deleteProfitDistribution } from "../../../services/ProfitDistribution/deleteProfitDistribution.service";
import AddProfitDistributionModal, {
  getAmountToDistributeError,
} from "./component/AddProfitDistributionModal";
import { toRetainedNetIncomeOption } from "./component/retainedNetIncomeOption";
import {
  confirmApproval,
  getApprovalBlockReason,
} from "./component/ProfitDistributionStatus";
import ProfitDistributionSummary from "./component/ProfitDistributionSummary";

const retainedNetIncomeColumns = [
  {
    name: "Transaction No.",
    selector: (row) => row.transaction_number,
    sortable: true,
    minWidth: "200px",
    cell: (row) => (
      <div className="text-nowrap fw-medium">{row.transaction_number}</div>
    ),
  },
  {
    name: "Name",
    selector: (row) => row.name,
    sortable: true,
    minWidth: "160px",
    cell: (row) => <div className="text-center">{row.name}</div>,
  },
  {
    name: "Period",
    selector: (row) => row.from,
    sortable: true,
    minWidth: "220px",
    cell: (row) => (
      <div className="text-center text-nowrap">
        {formatPeriod(row.from, row.to)}
      </div>
    ),
  },
  {
    name: "Cutoffs",
    selector: (row) => row.cutoffs.map((cutoff) => cutoff.name).join(", "),
    minWidth: "200px",
    cell: (row) => (
      <div className="d-flex flex-wrap justify-content-center gap-1 py-2">
        {row.cutoffs.map((cutoff) => (
          <span
            key={cutoff.id}
            className="badge bg-primary-subtle text-primary rounded-pill"
          >
            {cutoff.name}
          </span>
        ))}
      </div>
    ),
  },
  {
    name: "Net Income",
    selector: (row) => row.net_income,
    sortable: true,
    minWidth: "160px",
    cell: (row) => (
      <div
        className={`fw-semibold text-nowrap ${
          row.net_income < 0 ? "text-danger" : "text-success"
        }`}
      >
        {formatToPHP(row.net_income)}
      </div>
    ),
  },
];

function ViewProfitDistribution({ authrztn }) {
  const { id } = useParams();
  const userLoggedID = useDecodeToken();
  const navigate = useNavigate();

  const [distribution, setDistribution] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Edit modal
  const [showEdit, setShowEdit] = useState(false);
  const [validated, setValidated] = useState(false);
  const [form, setForm] = useState(null);
  const [retainedNetIncomeOptions, setRetainedNetIncomeOptions] = useState(
    [],
  );
  const [isOptionsLoading, setIsOptionsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);

  const fetchDistribution = async () => {
    setIsLoading(true);
    try {
      const data = await getProfitDistributionById(id);
      setDistribution(data);
    } catch (error) {
      console.log(error);
      setDistribution(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDistribution();
  }, [id]);

  // Linked Retained Net Incomes, each with its own net income
  const retainedNetIncomes = distribution?.retained_net_incomes || [];

  // Retained Net Incomes not used by another Profit Distribution (this one's
  // own stay selectable)
  const fetchRetainedNetIncomeOptions = async () => {
    setIsOptionsLoading(true);
    try {
      const [allRecords, distributions] = await Promise.all([
        getRetainedNetIncomes(),
        getProfitDistributions(),
      ]);
      const usedByOthers = new Set(
        distributions
          .filter((item) => item.id !== id && item.status !== "Rejected")
          .flatMap((item) =>
            item.profit_distribution_retained_net_incomes.map(
              (link) => link.retained_net_income_id,
            ),
          ),
      );
      setRetainedNetIncomeOptions(
        allRecords
          .filter((record) => !usedByOthers.has(record.id))
          .map(toRetainedNetIncomeOption),
      );
    } catch (error) {
      console.log(error);
    } finally {
      setIsOptionsLoading(false);
    }
  };

  const handleShowEdit = () => {
    setValidated(false);
    setForm({
      selectedRetainedNetIncomes: retainedNetIncomes.map(
        toRetainedNetIncomeOption,
      ),
      transactionNumber: distribution.transaction_number,
      distributionName: distribution.name,
      distributionDate: new Date(distribution.distribution_date),
      netIncomeAvailable: Number(distribution.net_income) || 0,
      amountToDistribute: String(Number(distribution.amount_to_distribute) || 0),
      remarks: distribution.remarks || "",
    });
    setShowEdit(true);
    fetchRetainedNetIncomeOptions();
  };

  const handleCloseEdit = () => {
    if (isSavingRef.current) return;
    setShowEdit(false);
    setValidated(false);
  };

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSavingRef.current) return;
    setValidated(true);

    if (
      form.selectedRetainedNetIncomes.length === 0 ||
      !form.distributionName.trim() ||
      !form.distributionDate
    ) {
      swal({
        icon: "error",
        title: "Fields are required",
        text: "Please fill in the red text fields.",
      });
      return;
    }

    const amountError = getAmountToDistributeError(form);
    if (amountError) {
      swal({ icon: "error", title: "Amount to Distribute", text: amountError });
      return;
    }

    swal({
      title: "Confirm Action",
      text: "Are you sure you want to update this Profit Distribution?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (confirmed) => {
      if (!confirmed || isSavingRef.current) return;
      isSavingRef.current = true;
      setIsSaving(true);

      const payload = {
        id,
        retained_net_income_ids: form.selectedRetainedNetIncomes.map(
          (option) => option.value,
        ),
        name: form.distributionName.trim(),
        distribution_date: format(
          new Date(form.distributionDate),
          "yyyy-MM-dd",
        ),
        amount_to_distribute: form.amountToDistribute,
        remarks: form.remarks.trim(),
        userLoggedID,
      };

      let result = null;
      try {
        result = await updateProfitDistribution(payload);
      } catch (error) {
        console.log(error);
        swal({
          icon: "error",
          title: "Something went wrong",
          text:
            error.response?.data?.message ||
            "Unable to update the Profit Distribution.",
        });
      } finally {
        isSavingRef.current = false;
        setIsSaving(false);
      }

      if (result) {
        swal({
          icon: "success",
          title: "Profit Distribution updated",
          text: result.message,
        });
        handleCloseEdit();
        fetchDistribution();
      }
    });
  };

  const handleApproval = async (action) => {
    const changed = await confirmApproval(distribution, action, userLoggedID);
    if (changed) fetchDistribution();
  };

  const handleDelete = () => {
    swal({
      title: "Confirm Action",
      text: `Are you sure you want to delete "${distribution.name}"? Its Retained Net Incomes will be available again for another Profit Distribution.`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (confirmed) => {
      if (!confirmed) return;

      try {
        await deleteProfitDistribution({ id, userLoggedID });
      } catch (error) {
        console.log(error);
        swal({
          icon: "error",
          title: "Something went wrong",
          text:
            error.response?.data?.message ||
            "Unable to delete the Profit Distribution.",
        });
        return;
      }

      swal({
        icon: "success",
        title: "Profit Distribution deleted",
        text: `"${distribution.name}" has been deleted.`,
        timer: 2000,
        button: false,
      });
      navigate("/accounting/profit-distribution");
    });
  };

  if (!authrztn.includes("Retained-View")) {
    return (
      <div className="no-access">
        <img src={NoAccess} alt="NoAccess" className="no-access-img" />
        <h3>You don't have access to this function.</h3>
      </div>
    );
  }

  const isPending = distribution?.status === "Pending";
  const canEdit = authrztn.includes("Retained-Edit") && isPending;
  const canDelete = authrztn.includes("Retained-Delete") && isPending;
  const canApprove = authrztn.includes("Retained-Approve") && isPending;
  // Shown but disabled when the distribution date is in a posted cutoff
  const approvalBlockReason = getApprovalBlockReason(distribution);

  return (
    <div className="h-100 w-100 border bg-white custom-container">
      <div className="w-100 p-2 d-flex flex-row justify-content-between">
        <div className="d-flex flex-column title-custom">
          <span className="fs-3">
            <Link
              to="/accounting/profit-distribution"
              className="text-dark mx-2"
            >
              <i className="fa-solid fa-arrow-left"></i>
            </Link>
            View Profit Distribution
          </span>
        </div>
        {(canEdit || canDelete || canApprove) && (
          <div className="d-flex gap-2">
            {canApprove && (
              // A disabled button doesn't fire hover events, so the reason
              // tooltip sits on this wrapper
              <span
                className="d-flex gap-2"
                title={approvalBlockReason || undefined}
              >
                <Button
                  className="btn d-flex align-items-center"
                  variant="outline-danger"
                  onClick={() => handleApproval("reject")}
                  disabled={Boolean(approvalBlockReason)}
                >
                  <i className="bx bx-x-circle fs-5 me-1"></i> Reject
                </Button>
                <Button
                  className="btn d-flex align-items-center"
                  variant="success"
                  onClick={() => handleApproval("approve")}
                  disabled={Boolean(approvalBlockReason)}
                >
                  <i className="bx bx-check-circle fs-5 me-1"></i> Approve
                </Button>
              </span>
            )}
            {canDelete && (
              <Button
                className="btn d-flex align-items-center"
                variant="outline-danger"
                onClick={handleDelete}
              >
                <i className="bx bx-trash fs-5 me-1"></i> Delete
              </Button>
            )}
            {canEdit && (
              <Button
                className="btn d-flex align-items-center title-button"
                variant="primary"
                onClick={handleShowEdit}
              >
                <i className="bx bx-edit fs-5 me-1"></i> Edit
              </Button>
            )}
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="w-100 p-4 d-flex justify-content-center">
          <ThreeDot color="#0d6efd" size="medium" text="" textColor="" />
        </div>
      ) : !distribution ? (
        <div className="w-100 p-4 d-flex flex-column align-items-center">
          <img src={NoData} alt="No Data" style={{ width: "150px" }} />
          <span className="text-muted mt-2">
            Profit Distribution not found.
          </span>
        </div>
      ) : (
        <div className="container-fluid mt-3">
          {canApprove && approvalBlockReason && (
            <div
              className="alert alert-warning d-flex align-items-start gap-2 py-2 px-3 mb-3"
              role="status"
            >
              <i className="bx bx-info-circle fs-5 flex-shrink-0"></i>
              <div className="small">
                <div className="fw-semibold">
                  Approve and Reject are disabled
                </div>
                {approvalBlockReason}
              </div>
            </div>
          )}

          <ProfitDistributionSummary distribution={distribution} />

          <h5 className="mb-2">Retained Net Incomes</h5>
          <DataTable
            columns={retainedNetIncomeColumns}
            data={retainedNetIncomes}
            customStyles={customStyles}
            className="dataTable"
            onRowClicked={(row) =>
              navigate(`/accounting/retained-net-income/${row.id}`)
            }
            pointerOnHover
            highlightOnHover
            noDataComponent={
              <div className="p-4 d-flex flex-column align-items-center">
                <img src={NoData} alt="No Data" style={{ width: "150px" }} />
                <span className="text-muted mt-2">
                  No Retained Net Income linked yet.
                </span>
              </div>
            }
          />
        </div>
      )}

      {form && (
        <AddProfitDistributionModal
          show={showEdit}
          onClose={handleCloseEdit}
          onSubmit={handleSubmit}
          form={form}
          setForm={setForm}
          onFieldChange={handleFieldChange}
          validated={validated}
          retainedNetIncomeOptions={retainedNetIncomeOptions}
          isLoading={isOptionsLoading}
          isSaving={isSaving}
          title="Edit Profit Distribution"
          submitLabel="Save Changes"
        />
      )}
    </div>
  );
}

export default ViewProfitDistribution;
